import { useState } from 'react';
import { motion } from 'framer-motion';
import { FaPlus, FaEdit, FaTrash, FaSave, FaTimes, FaSpinner } from 'react-icons/fa';
import { usePortfolio } from '../../context/PortfolioContext';
import { uploadAPI } from '../../utils/api.js';
import VisibilityToggle from './VisibilityToggle';

const emptySection = {
  section_name: '',
  section_title: '',
  content: '',
  image_url: '',
  visible: true
};

const SectionEditor = () => {
  const { portfolioData, updatePortfolioData, loading } = usePortfolio();
  const sections = portfolioData?.sections || [];
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [editingSection, setEditingSection] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newSection, setNewSection] = useState(emptySection);

  const handleAddSection = async () => {
    setIsSaving(true);
    setMessage('');
    try {
      const section = { ...newSection, visible: newSection.visible };
      await updatePortfolioData('sections', section);
      setNewSection(emptySection);
      setShowAddForm(false);
      setMessage('Section added successfully!');
    } catch (error) {
      setMessage('Error adding section');
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdateSection = async () => {
    setIsSaving(true);
    setMessage('');
    try {
      await updatePortfolioData('sections-update', editingSection, editingSection.id);
      setEditingSection(null);
      setMessage('Section updated successfully!');
    } catch (error) {
      setMessage('Error updating section');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteSection = async (id) => {
    if (!window.confirm('Are you sure you want to delete this section?')) return;
    try {
      await updatePortfolioData('sections-delete', {}, id);
      setMessage('Section deleted successfully!');
    } catch (error) {
      setMessage('Error deleting section');
    }
  };

  const handleImageUpload = async (file, sectionId = null) => {
    try {
      const response = await uploadAPI.uploadImage(file);
      if (response.success) {
        const imageUrl = response.data.url;
        if (sectionId && editingSection) {
          setEditingSection({ ...editingSection, image_url: imageUrl });
        } else {
          setNewSection({ ...newSection, image_url: imageUrl });
        }
        setMessage('Image uploaded successfully!');
      }
    } catch (error) {
      setMessage('Error uploading image');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <FaSpinner className="animate-spin text-4xl text-primary" />
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gray-800 rounded-lg p-6"
    >
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-xl font-semibold text-white">Sections Manager</h3>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setShowAddForm(true)}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
        >
          <FaPlus />
          Add Section
        </motion.button>
      </div>

      {message && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className={`mb-4 p-3 rounded-lg ${
            message.includes('Error') 
              ? 'bg-red-500/20 text-red-300 border border-red-500/30' 
              : 'bg-green-500/20 text-green-300 border border-green-500/30'
          }`}
        >
          {message}
        </motion.div>
      )}

      {/* Add Section Form */}
      {showAddForm && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          className="bg-gray-700 rounded-lg p-6 mb-8"
        >
          <div className="flex justify-between items-center mb-6">
            <h4 className="text-lg font-medium text-white">Add Section</h4>
            <button
              onClick={() => setShowAddForm(false)}
              className="p-2 text-gray-400 hover:text-white hover:bg-gray-600 rounded"
            >
              <FaTimes />
            </button>
          </div>
          <form onSubmit={e => { e.preventDefault(); handleAddSection(); }} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Section Name *</label>
              <input type="text" value={newSection.section_name} onChange={e => setNewSection({ ...newSection, section_name: e.target.value })} className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:outline-none focus:border-primary" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Section Title *</label>
              <input type="text" value={newSection.section_title} onChange={e => setNewSection({ ...newSection, section_title: e.target.value })} className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:outline-none focus:border-primary" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Content *</label>
              <textarea value={newSection.content} onChange={e => setNewSection({ ...newSection, content: e.target.value })} className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:outline-none focus:border-primary" rows={6} required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Image</label>
              <div className="flex items-center gap-4">
                <input type="file" accept="image/*" onChange={e => { const file = e.target.files[0]; if (file) handleImageUpload(file); }} className="flex-1 px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:outline-none focus:border-primary" />
                {newSection.image_url && (
                  <div className="flex items-center gap-2">
                    <img src={newSection.image_url} alt="Preview" className="w-12 h-12 object-cover rounded" />
                    <button type="button" onClick={() => setNewSection(prev => ({ ...prev, image_url: '' }))} className="p-1 text-red-400 hover:text-red-300"><FaTimes /></button>
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <VisibilityToggle checked={newSection.visible ?? true} onChange={e => setNewSection({ ...newSection, visible: e.target.checked })} loading={false} />
            </div>
            <div className="flex gap-3 pt-4">
              <motion.button type="submit" whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} disabled={isSaving} className="flex items-center gap-2 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50">
                {isSaving ? <FaSpinner className="animate-spin" /> : <FaSave />}
                {isSaving ? 'Adding...' : 'Add Section'}
              </motion.button>
              <motion.button type="button" whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} onClick={() => setShowAddForm(false)} className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors">
                Cancel
              </motion.button>
            </div>
          </form>
        </motion.div>
      )}

      {/* Edit Section Form */}
      {editingSection && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          className="bg-gray-700 rounded-lg p-6 mb-8"
        >
          <div className="flex justify-between items-center mb-6">
            <h4 className="text-lg font-medium text-white">Edit Section</h4>
            <button onClick={() => setEditingSection(null)} className="p-2 text-gray-400 hover:text-white hover:bg-gray-600 rounded"><FaTimes /></button>
          </div>
          <form onSubmit={e => { e.preventDefault(); handleUpdateSection(); }} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Section Name *</label>
              <input type="text" value={editingSection.section_name} onChange={e => setEditingSection({ ...editingSection, section_name: e.target.value })} className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:outline-none focus:border-primary" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Section Title *</label>
              <input type="text" value={editingSection.section_title} onChange={e => setEditingSection({ ...editingSection, section_title: e.target.value })} className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:outline-none focus:border-primary" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Content *</label>
              <textarea value={editingSection.content} onChange={e => setEditingSection({ ...editingSection, content: e.target.value })} className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:outline-none focus:border-primary" rows={6} required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Image</label>
              <div className="flex items-center gap-4">
                <input type="file" accept="image/*" onChange={e => { const file = e.target.files[0]; if (file) handleImageUpload(file, editingSection.id); }} className="flex-1 px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:outline-none focus:border-primary" />
                {editingSection.image_url && (
                  <div className="flex items-center gap-2">
                    <img src={editingSection.image_url} alt="Preview" className="w-12 h-12 object-cover rounded" />
                    <button type="button" onClick={() => setEditingSection(prev => ({ ...prev, image_url: '' }))} className="p-1 text-red-400 hover:text-red-300"><FaTimes /></button>
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <VisibilityToggle checked={editingSection.visible ?? true} onChange={e => setEditingSection({ ...editingSection, visible: e.target.checked })} loading={false} />
            </div>
            <div className="flex gap-3 pt-4">
              <motion.button type="submit" whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} disabled={isSaving} className="flex items-center gap-2 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50">
                {isSaving ? <FaSpinner className="animate-spin" /> : <FaSave />}
                {isSaving ? 'Saving...' : 'Save Changes'}
              </motion.button>
              <motion.button type="button" whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} onClick={() => setEditingSection(null)} className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors">
                Cancel
              </motion.button>
            </div>
          </form>
        </motion.div>
      )}

      {/* Section List */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {sections.map((section, idx) => (
          <div key={section.id || idx} className="bg-gray-700 rounded p-4 flex flex-col">
            <div className="flex items-center gap-2 mb-2">
              <h4 className="font-semibold text-white flex-1">{section.section_title}</h4>
              <button onClick={() => setEditingSection(section)} className="text-yellow-400 hover:text-yellow-300"><FaEdit /></button>
              <button onClick={() => handleDeleteSection(section.id)} className="text-red-400 hover:text-red-300"><FaTrash /></button>
            </div>
            <div className="text-gray-400 text-xs mb-2">{section.section_name}</div>
            <div className="mb-2">
              {section.image_url && <img src={section.image_url} alt={section.section_title} className="w-full h-32 object-cover rounded mb-2" />}
              <div className="text-gray-300 text-sm mb-2">{section.content.slice(0, 100)}...</div>
              <div className="flex items-center gap-2">
                <VisibilityToggle checked={section.visible ?? true} onChange={e => { const updatedSection = { ...section, visible: e.target.checked }; updatePortfolioData('sections-update', updatedSection, section.id); }} loading={false} />
              </div>
            </div>
          </div>
        ))}
      </div>
    </motion.div>
  );
};

export default SectionEditor; 